import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

const STATUS_COLORS = {
  'Pending Review': 'badge-amber',
  'Approved': 'badge-green',
  'Denied': 'badge-red',
  'Invoiced': 'badge-blue',
  'Delivered': 'badge-green',
  'Cancelled': 'badge-grey',
}

const STATUS_FLOW = ['Pending Review', 'Approved', 'Invoiced', 'Delivered']

export default function SalesOrders() {
  const { user, profile, isAdmin, isAdminOrManager } = useAuth()
  const [orders, setOrders] = useState([])
  const [customers, setCustomers] = useState([])
  const [stores, setStores] = useState([])
  const [tab, setTab] = useState('list')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [selected, setSelected] = useState(null) // order being edited/viewed
  const [denyModal, setDenyModal] = useState(null)
  const [denyReason, setDenyReason] = useState('')
  const [cancelModal, setCancelModal] = useState(null)
  const [cancelReason, setCancelReason] = useState('')
  const [overrideModal, setOverrideModal] = useState(null)
  const [overrideReason, setOverrideReason] = useState('')
  const [approveDeliveryDate, setApproveDeliveryDate] = useState('')
  const [creditWarning, setCreditWarning] = useState(null)
  const [filterStatus, setFilterStatus] = useState('Active')
  const [analyticsTab, setAnalyticsTab] = useState(false)

  const emptyItem = () => ({ description: '', crop: '', variety: '', qty_kg: '', unit_price: '', total: '' })
  const emptyForm = () => ({
    customer_id: '', customer_name: '', customer_email: '',
    payment_type: 'Account', // 'Account' | 'Cash'
    order_date: ugandaDate(),
    requested_delivery_date: '',
    payment_terms: 30,
    notes: '',
    items: [emptyItem()],
  })
  const [form, setForm] = useState(emptyForm())

  useEffect(() => { loadAll() }, [])

  const loadAll = async () => {
    setLoading(true)
    const [ordersRes, custRes, storesRes] = await Promise.all([
      supabase.from('sales_orders')
        .select('*, profiles!created_by(full_name), approver:profiles!approved_by(full_name)')
        .order('order_date', { ascending: false }),
      // Try customer_balances first, fall back to customers table
      supabase.from('customer_balances').select('*').order('name')
        .then(res => res.data?.length ? res : supabase.from('customers').select('*').order('name')),
      supabase.from('stock_balances').select('*').gt('current_balance', 0),
    ])
    setOrders(ordersRes.data || [])
    setCustomers(custRes.data || [])
    setStores(storesRes.data || [])
    setLoading(false)
  }

  const handleCustomerSelect = (customerId) => {
    if (!customerId) { setForm(f => ({ ...f, customer_id: '', customer_name: '', customer_email: '', payment_terms: 30, payment_type: 'Account' })); setCreditWarning(null); return }
    const c = customers.find(c => c.id === customerId)
    if (!c) return
    setForm(f => ({
      ...f, customer_id: customerId, customer_name: c.name, customer_email: c.email || '',
      payment_terms: c.payment_terms || 30,
      payment_type: c.payment_terms === 0 ? 'Cash' : 'Account',
    }))
    const warnings = []
    if (c.over_credit_limit) warnings.push({ type: 'error', text: `🚫 Over credit limit (UGX ${(c.credit_limit||0).toLocaleString()}). Outstanding: UGX ${(c.outstanding_balance||0).toLocaleString()}. Finance must override to approve.` })
    if (c.days_oldest_unpaid > (c.payment_terms || 30)) warnings.push({ type: 'warning', text: `⚠️ Has invoices ${c.days_oldest_unpaid - (c.payment_terms||30)} days overdue. Finance will be alerted.` })
    if (c.is_on_hold) warnings.push({ type: 'error', text: `🚫 On hold: ${c.hold_reason || 'Contact management.'}` })
    if (c.payment_terms === 0) warnings.push({ type: 'info', text: `ℹ️ Cash customer — payment required on delivery. If cash not provided, driver must return goods and sale will be cancelled automatically.` })
    setCreditWarning(warnings.length > 0 ? warnings : null)
  }

  const setItem = (i, field, val) => {
    const items = [...form.items]
    items[i] = { ...items[i], [field]: val }
    if (field === 'qty_kg' || field === 'unit_price') {
      const qty = parseFloat(field === 'qty_kg' ? val : items[i].qty_kg) || 0
      const price = parseFloat(field === 'unit_price' ? val : items[i].unit_price) || 0
      items[i].total = (qty * price).toFixed(0)
    }
    setForm(f => ({ ...f, items }))
  }

  const totalAmount = form.items.reduce((s, r) => s + (parseFloat(r.total) || 0), 0)

  const generateSONumber = async () => {
    const { data } = await supabase.from('sales_orders').select('so_number').order('created_at', { ascending: false }).limit(1)
    const last = data?.[0]?.so_number
    if (last) { const num = parseInt(last.replace('SO-', '')) + 1; return `SO-${String(num).padStart(4, '0')}` }
    return 'SO-0001'
  }

  const handleNew = () => { setSelected(null); setForm(emptyForm()); setCreditWarning(null); setTab('form') }

  const handleEdit = (order) => {
    if (!['Pending Review'].includes(order.status)) { setMsg({ type: 'error', text: 'Only Pending Review orders can be edited.' }); return }
    setSelected(order)
    setForm({
      customer_id: order.customer_id || '',
      customer_name: order.customer_name,
      customer_email: order.customer_email || '',
      payment_type: order.payment_type || 'Account',
      order_date: order.order_date,
      requested_delivery_date: order.requested_delivery_date || '',
      payment_terms: order.payment_terms || 30,
      notes: order.notes || '',
      items: order.items || [emptyItem()],
    })
    setCreditWarning(null)
    setTab('form')
  }

  const handleSave = async () => {
    if (!form.customer_name) { setMsg({ type: 'error', text: 'Customer is required.' }); return }
    if (!form.items.filter(i => i.description).length) { setMsg({ type: 'error', text: 'Add at least one item.' }); return }
    const c = customers.find(c => c.id === form.customer_id)
    if (c?.is_on_hold && !isAdminOrManager) { setMsg({ type: 'error', text: 'This customer is on hold. Only managers can raise orders.' }); return }
    setSaving(true); setMsg(null)
    const validItems = form.items.filter(i => i.description)
    const total = validItems.reduce((s, r) => s + (parseFloat(r.total) || 0), 0)
    if (selected) {
      // Edit existing
      const { error } = await supabase.from('sales_orders').update({
        customer_id: form.customer_id || null,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        payment_type: form.payment_type,
        order_date: form.order_date,
        requested_delivery_date: form.requested_delivery_date || null,
        payment_terms: parseInt(form.payment_terms),
        total_amount: total,
        items: validItems,
        notes: form.notes,
        updated_at: new Date().toISOString(),
      }).eq('id', selected.id)
      if (error) { setMsg({ type: 'error', text: error.message }); setSaving(false); return }
      setMsg({ type: 'success', text: `Sales Order ${selected.so_number} updated.` })
    } else {
      // New order
      const soNumber = await generateSONumber()
      const { error } = await supabase.from('sales_orders').insert({
        so_number: soNumber,
        customer_id: form.customer_id || null,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        payment_type: form.payment_type,
        order_date: form.order_date,
        requested_delivery_date: form.requested_delivery_date || null,
        payment_terms: parseInt(form.payment_terms),
        total_amount: total,
        status: 'Pending Review',
        items: validItems,
        notes: form.notes,
        created_by: user.id,
      })
      if (error) { setMsg({ type: 'error', text: error.message }); setSaving(false); return }
      setMsg({ type: 'success', text: `Sales Order ${soNumber} submitted for finance review.` })
    }
    setSaving(false); setTab('list'); setSelected(null); setCreditWarning(null); loadAll()
    setTimeout(() => setMsg(null), 4000)
  }

  const handleApprove = (order) => {
    const c = customers.find(c => c.id === order.customer_id)
    setApproveDeliveryDate(order.requested_delivery_date || '')
    if (c?.over_credit_limit && !isAdmin) {
      setOverrideModal(order); setOverrideReason(''); return
    }
    setOverrideModal({ ...order, _justApprove: true }); setOverrideReason('')
  }

  const doApprove = async (order, deliveryDate, override) => {
    setSaving(true)
    await supabase.from('sales_orders').update({
      status: 'Approved',
      approved_by: user.id,
      approved_at: new Date().toISOString(),
      confirmed_delivery_date: deliveryDate || order.requested_delivery_date,
      override_reason: override || null,
    }).eq('id', order.id)

    // Auto-generate invoice
    const { data: lastInv } = await supabase.from('invoices').select('invoice_number').order('created_at', { ascending: false }).limit(1)
    const lastNum = lastInv?.[0]?.invoice_number
    let invNum = 'INV-0001'
    if (lastNum) { const n = parseInt(lastNum.replace('INV-', '')) + 1; invNum = `INV-${String(n).padStart(4, '0')}` }

    const dueDate = new Date(order.order_date)
    dueDate.setDate(dueDate.getDate() + (order.payment_terms || 30))

    const { data: inv } = await supabase.from('invoices').insert({
      invoice_number: invNum,
      invoice_date: ugandaDate(),
      due_date: ugandaDate(dueDate),
      customer_id: order.customer_id,
      customer_name: order.customer_name,
      customer_email: order.customer_email,
      amount: order.total_amount,
      balance_due: order.total_amount,
      status: 'Unpaid',
      items: order.items,
      notes: `Auto-generated from ${order.so_number}${order.payment_type === 'Cash' ? ' — CASH ON DELIVERY. If unpaid on delivery, return goods and cancel.' : ''}`,
      sales_order_id: order.id,
      user_id: user.id,
    }).select().single()

    if (inv) {
      await supabase.from('sales_orders').update({ invoice_id: inv.id, invoice_number: invNum }).eq('id', order.id)
      // Auto-generate delivery note
      const dnNum = `DN-${invNum.replace('INV-', '')}`
      const { data: dn } = await supabase.from('delivery_notes').insert({
        dn_number: dnNum,
        invoice_id: inv.id,
        sales_order_id: order.id,
        customer_name: order.customer_name,
        delivery_date: deliveryDate || order.requested_delivery_date,
        status: 'Pending',
        items: order.items,
        notes: order.payment_type === 'Cash' ? '⚠️ CASH ON DELIVERY — collect payment before handing over goods. If customer cannot pay, return all goods immediately.' : '',
        created_by: user.id,
      }).select().single()
      if (dn) await supabase.from('sales_orders').update({ delivery_note_id: dn.id }).eq('id', order.id)
    }

    setSaving(false); setOverrideModal(null); setOverrideReason('')
    setMsg({ type: 'success', text: `✅ Approved. Invoice ${invNum} and delivery note auto-generated. Logistics team can now access the delivery note.` })
    loadAll(); setTimeout(() => setMsg(null), 6000)
  }

  const handleDeny = async () => {
    if (!denyReason.trim()) return
    await supabase.from('sales_orders').update({
      status: 'Denied',
      denial_reason: denyReason,
      approved_by: user.id,
      approved_at: new Date().toISOString(),
    }).eq('id', denyModal.id)
    setDenyModal(null); setDenyReason('')
    setMsg({ type: 'success', text: 'Order denied. Reason recorded for audit trail.' })
    loadAll(); setTimeout(() => setMsg(null), 3000)
  }

  const handleCancel = async () => {
    if (!cancelReason.trim()) return
    await supabase.from('sales_orders').update({
      status: 'Cancelled',
      cancellation_reason: cancelReason,
      cancelled_by: user.id,
      cancelled_at: new Date().toISOString(),
    }).eq('id', cancelModal.id)
    // If invoice was generated, mark it cancelled/void
    if (cancelModal.invoice_id) {
      await supabase.from('invoices').update({ status: 'Void', notes: `Voided — Sales order ${cancelModal.so_number} cancelled by customer. Reason: ${cancelReason}` }).eq('id', cancelModal.invoice_id)
    }
    setCancelModal(null); setCancelReason('')
    setMsg({ type: 'success', text: 'Order cancelled. Recorded in audit trail. Any linked invoice has been voided.' })
    loadAll(); setTimeout(() => setMsg(null), 4000)
  }

  // Analytics calculations
  const analytics = React.useMemo(() => {
    const total = orders.length
    const approved = orders.filter(o => ['Approved','Invoiced','Delivered'].includes(o.status)).length
    const denied = orders.filter(o => o.status === 'Denied').length
    const cancelled = orders.filter(o => o.status === 'Cancelled').length
    const pending = orders.filter(o => o.status === 'Pending Review').length
    const conversionRate = total > 0 ? Math.round((approved / (total - pending)) * 100) : 0
    const totalRevenue = orders.filter(o => ['Approved','Invoiced','Delivered'].includes(o.status))
      .reduce((s, o) => s + (parseFloat(o.total_amount) || 0), 0)
    // Average time from order to approval
    const approvedWithTimes = orders.filter(o => o.approved_at && o.created_at && o.status !== 'Denied')
    const avgApprovalHours = approvedWithTimes.length > 0
      ? Math.round(approvedWithTimes.reduce((s, o) => s + (new Date(o.approved_at) - new Date(o.created_at)) / 3600000, 0) / approvedWithTimes.length)
      : null
    // Cash vs account
    const cashOrders = orders.filter(o => o.payment_type === 'Cash').length
    const accountOrders = orders.filter(o => o.payment_type === 'Account').length
    // Top customers
    const byCustomer = {}
    orders.filter(o => ['Approved','Invoiced','Delivered'].includes(o.status)).forEach(o => {
      byCustomer[o.customer_name] = (byCustomer[o.customer_name] || 0) + (parseFloat(o.total_amount) || 0)
    })
    const topCustomers = Object.entries(byCustomer).sort(([,a],[,b]) => b-a).slice(0,5)
    return { total, approved, denied, cancelled, pending, conversionRate, totalRevenue, avgApprovalHours, cashOrders, accountOrders, topCustomers }
  }, [orders])

  const filteredOrders = React.useMemo(() => {
    if (filterStatus === 'Active') return orders.filter(o => !['Cancelled','Denied'].includes(o.status))
    if (filterStatus === 'All') return orders
    return orders.filter(o => o.status === filterStatus)
  }, [orders, filterStatus])

  const printOrder = (order) => {
    const w = window.open('', '_blank')
    w.document.write(`<html><head><title>${order.so_number}</title>
    <style>body{font-family:Arial,sans-serif;padding:32px;color:#1C3520;max-width:700px;margin:0 auto;}
    h2{font-size:1.3rem;} table{width:100%;border-collapse:collapse;margin-top:14px;}
    th{background:#f0ede8;padding:8px;text-align:left;font-size:0.8rem;} td{padding:8px;border-bottom:1px solid #eee;font-size:0.85rem;}
    .warn{background:#fff8e6;border:1px solid #D4B96A;padding:10px;border-radius:6px;margin-top:12px;font-size:0.82rem;}
    .footer{margin-top:32px;font-size:0.72rem;color:#888;}</style></head><body>
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">
      <div><h2>Sales Order — ${order.so_number}</h2><div style="color:#666;font-size:0.82rem;">Status: ${order.status}</div></div>
      <div style="font-size:0.75rem;color:#666;text-align:right;">Kobflo Greentek Ltd<br/>app.kobfloagri.com</div>
    </div>
    <p><strong>Customer:</strong> ${order.customer_name}<br/>
    <strong>Order Date:</strong> ${new Date(order.order_date).toLocaleDateString('en-UG')}<br/>
    <strong>Requested Delivery:</strong> ${order.requested_delivery_date ? new Date(order.requested_delivery_date).toLocaleDateString('en-UG') : '—'}<br/>
    <strong>Payment:</strong> ${order.payment_type === 'Cash' ? 'Cash on Delivery' : `Net ${order.payment_terms} days`}<br/>
    ${order.confirmed_delivery_date ? `<strong>Confirmed Delivery:</strong> ${new Date(order.confirmed_delivery_date).toLocaleDateString('en-UG')}<br/>` : ''}
    ${order.invoice_number ? `<strong>Invoice:</strong> ${order.invoice_number}<br/>` : ''}</p>
    <table><thead><tr><th>Description</th><th>Variety</th><th>Qty (kg)</th><th>Unit Price</th><th>Total</th></tr></thead>
    <tbody>${(order.items||[]).map(i=>`<tr><td>${i.description}</td><td>${i.variety||'—'}</td><td>${i.qty_kg}</td><td>UGX ${parseFloat(i.unit_price||0).toLocaleString()}</td><td>UGX ${parseFloat(i.total||0).toLocaleString()}</td></tr>`).join('')}</tbody>
    </table>
    <p style="font-weight:700;margin-top:14px;font-size:1.1rem;">Total: UGX ${parseFloat(order.total_amount||0).toLocaleString()}</p>
    ${order.payment_type==='Cash'?'<div class="warn">⚠️ CASH ON DELIVERY — collect payment before handing over goods. If customer cannot pay, return all goods immediately and report to management.</div>':''}
    ${order.notes?`<p><em>Notes: ${order.notes}</em></p>`:''}
    <div class="footer">Generated ${new Date().toLocaleDateString('en-UG')} · Kobflo Greentek Ltd</div>
    </body></html>`)
    w.print()
  }

  return (
    <AppLayout title="Sales Orders">
      <div className="section-header">
        <div className="section-icon">🛒</div>
        <div className="section-info">
          <h2>Sales Orders</h2>
          <p>Raise order → finance approves → invoice + delivery note auto-generated. Full audit trail kept.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
          <button className={`btn ${analyticsTab ? 'btn-primary' : 'btn-secondary'} btn-sm`} onClick={() => setAnalyticsTab(a => !a)}>📊 Analytics</button>
          {tab === 'list' && <button className="btn btn-primary" onClick={handleNew}>+ New Order</button>}
          {tab !== 'list' && <button className="btn btn-ghost" onClick={() => { setTab('list'); setSelected(null); setCreditWarning(null) }}>← Back</button>}
        </div>
      </div>

      {msg && <div className={`alert ${msg.type === 'success' ? 'alert-green' : 'alert-red'}`}>{msg.text}</div>}

      {/* ANALYTICS PANEL */}
      {analyticsTab && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title">📊 Sales Order Analytics</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Total Orders', value: analytics.total, color: 'var(--text)' },
              { label: 'Conversion Rate', value: `${analytics.conversionRate}%`, color: analytics.conversionRate >= 70 ? 'var(--green-mid)' : 'var(--amber)', note: 'Approved ÷ Decided' },
              { label: 'Avg Approval Time', value: analytics.avgApprovalHours !== null ? `${analytics.avgApprovalHours}h` : '—', color: 'var(--blue)', note: 'Order to approval' },
              { label: 'Total Revenue (approved)', value: `UGX ${analytics.totalRevenue.toLocaleString()}`, color: 'var(--green-deep)' },
            ].map(({ label, value, color, note }) => (
              <div key={label} style={{ background: 'var(--cream)', borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', fontFamily: "'DM Mono',monospace" }}>{label}</div>
                <div style={{ fontWeight: 700, color, fontFamily: "'Cormorant Garamond',serif", fontSize: '1.1rem' }}>{value}</div>
                {note && <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: 2 }}>{note}</div>}
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, marginBottom: 8, color: 'var(--green-deep)' }}>Order Status Breakdown</div>
              {[
                { label: 'Pending Review', count: analytics.pending, color: 'var(--amber)' },
                { label: 'Approved / Invoiced / Delivered', count: analytics.approved, color: 'var(--green-mid)' },
                { label: 'Denied by Finance', count: analytics.denied, color: 'var(--red)' },
                { label: 'Cancelled by Customer', count: analytics.cancelled, color: 'var(--text-muted)' },
              ].map(({ label, count, color }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: '0.78rem' }}>
                  <span style={{ color }}>{label}</span>
                  <span style={{ fontWeight: 700 }}>{count}</span>
                </div>
              ))}
              <div style={{ marginTop: 10, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Cash orders: {analytics.cashOrders} · Account orders: {analytics.accountOrders}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, marginBottom: 8, color: 'var(--green-deep)' }}>Top Customers by Revenue</div>
              {analytics.topCustomers.length === 0
                ? <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>No approved orders yet</div>
                : analytics.topCustomers.map(([name, amount], i) => (
                  <div key={name} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: '0.78rem' }}>
                    <span>{i+1}. {name}</span>
                    <span style={{ fontWeight: 700, fontFamily: "'DM Mono',monospace" }}>UGX {amount.toLocaleString()}</span>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {/* LIST */}
      {tab === 'list' && (
        <div className="card">
          {/* Status filter */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {['Active', 'All', 'Pending Review', 'Approved', 'Denied', 'Cancelled'].map(s => (
              <button key={s} onClick={() => setFilterStatus(s)}
                style={{ padding: '5px 12px', borderRadius: 20, fontSize: '0.72rem', fontWeight: filterStatus===s?700:400, cursor: 'pointer', border: `1.5px solid ${filterStatus===s?'var(--amber)':'var(--border)'}`, background: filterStatus===s?'var(--amber-pale)':'white', color: filterStatus===s?'var(--amber)':'var(--text-muted)', fontFamily: 'inherit' }}>
                {s} {s !== 'All' && s !== 'Active' ? `(${orders.filter(o => o.status === s).length})` : ''}
              </button>
            ))}
          </div>

          {loading ? <div style={{ textAlign: 'center', padding: 32 }}><span className="spinner dark" /></div>
          : filteredOrders.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🛒</div>
              <h3>No sales orders</h3>
              <p>Create a sales order. Finance reviews it — then invoice and delivery note are auto-generated on approval.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>SO #</th><th>Customer</th><th>Date</th><th>Payment</th><th>Amount</th><th>Status</th><th>Invoice</th><th>Delivery</th><th>By</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {filteredOrders.map(o => (
                    <tr key={o.id} style={{ opacity: ['Cancelled','Denied'].includes(o.status) ? 0.6 : 1 }}>
                      <td style={{ fontFamily: "'DM Mono',monospace", fontWeight: 700, fontSize: '0.82rem' }}>{o.so_number}</td>
                      <td style={{ fontWeight: 600 }}>{o.customer_name}</td>
                      <td style={{ fontSize: '0.75rem' }}>{new Date(o.order_date).toLocaleDateString('en-UG')}</td>
                      <td><span className={`badge ${o.payment_type==='Cash'?'badge-amber':'badge-grey'}`} style={{ fontSize:'0.65rem' }}>{o.payment_type==='Cash'?'Cash on Delivery':'Account'}</span></td>
                      <td style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.82rem' }}>UGX {parseFloat(o.total_amount||0).toLocaleString()}</td>
                      <td>
                        <div><span className={`badge ${STATUS_COLORS[o.status]||'badge-grey'}`}>{o.status}</span></div>
                        {o.denial_reason && <div style={{ fontSize:'0.65rem', color:'var(--red)', marginTop:2 }} title={o.denial_reason}>Reason: {o.denial_reason.slice(0,35)}...</div>}
                        {o.cancellation_reason && <div style={{ fontSize:'0.65rem', color:'var(--text-muted)', marginTop:2 }} title={o.cancellation_reason}>Cancelled: {o.cancellation_reason.slice(0,35)}...</div>}
                      </td>
                      <td style={{ fontSize: '0.72rem', color: o.invoice_number ? 'var(--green-mid)' : 'var(--text-muted)' }}>{o.invoice_number || '—'}</td>
                      <td style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{o.confirmed_delivery_date ? new Date(o.confirmed_delivery_date).toLocaleDateString('en-UG') : '—'}</td>
                      <td style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{o.profiles?.full_name?.split(' ')[0] || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => printOrder(o)} title="Print">🖨️</button>
                          {o.status === 'Pending Review' && (
                            <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(o)}>✏️ Edit</button>
                          )}
                          {o.status === 'Pending Review' && isAdminOrManager && (
                            <>
                              <button className="btn btn-primary btn-sm" onClick={() => handleApprove(o)} disabled={saving}>✅</button>
                              <button className="btn btn-danger btn-sm" onClick={() => { setDenyModal(o); setDenyReason('') }}>✗</button>
                            </>
                          )}
                          {!['Cancelled','Denied','Delivered'].includes(o.status) && (
                            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', fontSize: '0.7rem' }} onClick={() => { setCancelModal(o); setCancelReason('') }}>Cancel</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ORDER FORM — new or edit */}
      {tab === 'form' && (
        <div className="card">
          <div className="card-title">{selected ? `Edit Order — ${selected.so_number}` : 'New Sales Order'}</div>

          {selected && (
            <div className="alert alert-amber" style={{ marginBottom: 14, fontSize: '0.78rem' }}>
              ✏️ Editing <strong>{selected.so_number}</strong> — only Pending Review orders can be edited. Once approved, contact finance to make changes.
            </div>
          )}

          {creditWarning && creditWarning.map((w, i) => (
            <div key={i} className={`alert ${w.type === 'error' ? 'alert-red' : w.type === 'warning' ? 'alert-amber' : 'alert-green'}`} style={{ fontSize: '0.8rem' }}>{w.text}</div>
          ))}

          <div className="grid-2" style={{ marginBottom: 14 }}>
            <div className="form-group"><label className="form-label">Customer *</label>
              <select className="form-select" value={form.customer_id} onChange={e => handleCustomerSelect(e.target.value)}>
                <option value="">— Select customer —</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.is_on_hold ? ' 🚫 ON HOLD' : c.over_credit_limit ? ' ⚠️ OVER LIMIT' : ''}
                    {c.payment_terms === 0 ? ' [Cash on Delivery]' : c.payment_terms ? ` [Net ${c.payment_terms}d]` : ''}
                  </option>
                ))}
              </select>
              {customers.length === 0 && (
                <div style={{ fontSize: '0.72rem', color: 'var(--red)', marginTop: 4 }}>
                  No customers found. Add customers in the Customers page first.
                </div>
              )}
            </div>
            <div className="form-group"><label className="form-label">Payment Type</label>
              <div style={{ padding: '10px 14px', background: form.payment_type === 'Cash' ? 'var(--amber-pale)' : 'var(--green-pale)', borderRadius: 6, fontWeight: 700, fontSize: '0.85rem', border: `1.5px solid ${form.payment_type === 'Cash' ? 'var(--amber)' : 'var(--green-mid)'}` }}>
                {form.payment_type === 'Cash' ? '⚠️ Cash on Delivery — must collect before handing goods' : `✅ Account — Net ${form.payment_terms} days`}
              </div>
            </div>
            <div className="form-group"><label className="form-label">Order Date</label>
              <input className="form-input" type="date" value={form.order_date} onChange={e => setForm(f => ({ ...f, order_date: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Requested Delivery Date</label>
              <input className="form-input" type="date" value={form.requested_delivery_date} onChange={e => setForm(f => ({ ...f, requested_delivery_date: e.target.value }))} /></div>
          </div>

          {stores.length > 0 && (
            <div className="alert alert-green" style={{ marginBottom: 14, fontSize: '0.78rem' }}>
              📦 <strong>Available in Stores:</strong> {stores.map(s => `${s.name}: ${parseFloat(s.current_balance).toFixed(1)} ${s.unit}`).join(' · ')}
            </div>
          )}

          <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 8 }}>Order Items</div>
          <div className="table-wrap" style={{ marginBottom: 8 }}>
            <table>
              <thead><tr><th>Crop / Product</th><th>Variety</th><th>Qty (kg)</th><th>Unit Price (UGX/kg)</th><th>Total (UGX)</th><th style={{ width: 32 }}></th></tr></thead>
              <tbody>
                {form.items.map((item, i) => (
                  <tr key={i}>
                    <td><input className="form-input" placeholder="e.g. Lettuce" value={item.description} onChange={e => setItem(i, 'description', e.target.value)} /></td>
                    <td><input className="form-input" placeholder="e.g. Butterhead" value={item.variety||''} onChange={e => setItem(i, 'variety', e.target.value)} /></td>
                    <td><input className="form-input" type="number" step="0.01" style={{ width: 80 }} value={item.qty_kg||''} onChange={e => setItem(i, 'qty_kg', e.target.value)} /></td>
                    <td><input className="form-input" type="number" style={{ width: 110 }} value={item.unit_price||''} onChange={e => setItem(i, 'unit_price', e.target.value)} /></td>
                    <td style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>{parseFloat(item.total||0).toLocaleString()}</td>
                    <td>{form.items.length > 1 && <button className="btn-del-row" onClick={() => setForm(f => ({ ...f, items: f.items.filter((_, j) => j !== i) }))}>✕</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }))}>+ Add Item</button>

          <div style={{ background: 'var(--green-pale)', borderRadius: 8, padding: '12px 16px', margin: '14px 0', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700 }}>Total Order Value</span>
            <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.2rem', fontWeight: 700, color: 'var(--green-deep)' }}>UGX {totalAmount.toLocaleString()}</span>
          </div>

          <div className="form-group" style={{ marginBottom: 16 }}>
            <label className="form-label">Notes / Special Instructions</label>
            <textarea className="form-textarea" style={{ minHeight: 60 }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Packaging requirements, delivery instructions, etc." />
          </div>

          {!selected && (
            <div className="alert alert-amber" style={{ marginBottom: 16, fontSize: '0.78rem' }}>
              📋 This order will go to <strong>Finance for review</strong>. On approval, the invoice and delivery note are automatically generated.
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner" />Saving...</> : selected ? '💾 Save Changes' : '📤 Submit for Finance Review'}
            </button>
            <button className="btn btn-ghost" onClick={() => { setTab('list'); setSelected(null); setCreditWarning(null) }}>Cancel</button>
          </div>
        </div>
      )}

      {/* APPROVE MODAL — confirm delivery date */}
      {overrideModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setOverrideModal(null)}>
          <div className="modal">
            <div className="modal-title">{overrideModal._justApprove ? '✅ Approve Sales Order' : '⚠️ Credit Override Required'}</div>
            <div className="modal-subtitle"><strong>{overrideModal.so_number}</strong> — {overrideModal.customer_name} — UGX {parseFloat(overrideModal.total_amount||0).toLocaleString()}</div>
            {!overrideModal._justApprove && (
              <div className="alert alert-red">This customer is over their credit limit. Only Edward or Sharon can approve. Provide a business justification.</div>
            )}
            <div className="form-group">
              <label className="form-label">Confirmed Delivery Date</label>
              <input className="form-input" type="date" value={approveDeliveryDate} onChange={e => setApproveDeliveryDate(e.target.value)} />
            </div>
            {!overrideModal._justApprove && (
              <div className="form-group">
                <label className="form-label">Override Reason * (min 20 characters)</label>
                <textarea className="form-textarea" style={{ minHeight: 80 }} value={overrideReason} onChange={e => setOverrideReason(e.target.value)} placeholder="Business justification for approving despite credit limit breach..." autoFocus />
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setOverrideModal(null)}>Cancel</button>
              <button className="btn btn-primary"
                onClick={() => doApprove(overrideModal, approveDeliveryDate, overrideReason || null)}
                disabled={saving || (!overrideModal._justApprove && overrideReason.length < 20)}>
                {saving ? <><span className="spinner" />Processing...</> : '✅ Approve & Generate Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DENY MODAL */}
      {denyModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDenyModal(null)}>
          <div className="modal">
            <div className="modal-title">✗ Deny Sales Order</div>
            <div className="modal-subtitle"><strong>{denyModal.so_number}</strong> — {denyModal.customer_name}</div>
            {/* Auto-suggest reason based on customer status */}
            {(() => {
              const c = customers.find(c => c.id === denyModal.customer_id)
              const suggestions = []
              if (c?.over_credit_limit) suggestions.push(`Customer is over their credit limit of UGX ${(c.credit_limit||0).toLocaleString()}. Outstanding balance: UGX ${(c.outstanding_balance||0).toLocaleString()}.`)
              if (c?.days_oldest_unpaid > (c?.payment_terms||30)) suggestions.push(`Customer has overdue invoices (${c.days_oldest_unpaid} days). Payment must be received before further orders are approved.`)
              if (c?.is_on_hold) suggestions.push(`Customer account is on hold: ${c.hold_reason || 'Management decision.'}`)
              if (suggestions.length > 0) return (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 6 }}>Suggested reasons (click to use):</div>
                  {suggestions.map((s, i) => (
                    <div key={i} onClick={() => setDenyReason(s)} style={{ padding: '6px 10px', background: 'var(--red-pale)', borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer', marginBottom: 4, color: 'var(--red)', border: '1px solid var(--red)' }}>
                      {s}
                    </div>
                  ))}
                </div>
              )
              return null
            })()}
            <div className="form-group">
              <label className="form-label">Reason for Denial *</label>
              <textarea className="form-textarea" style={{ minHeight: 80 }} value={denyReason} onChange={e => setDenyReason(e.target.value)} placeholder="e.g. Insufficient stock, pricing not agreed, customer over credit limit..." autoFocus />
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setDenyModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeny} disabled={!denyReason.trim()}>✗ Deny Order</button>
            </div>
          </div>
        </div>
      )}

      {/* CANCEL MODAL (sales team) */}
      {cancelModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setCancelModal(null)}>
          <div className="modal">
            <div className="modal-title">🚫 Cancel Sales Order</div>
            <div className="modal-subtitle"><strong>{cancelModal.so_number}</strong> — {cancelModal.customer_name}</div>
            {cancelModal.invoice_id && (
              <div className="alert alert-amber" style={{ fontSize: '0.78rem' }}>⚠️ This order has an invoice ({cancelModal.invoice_number}). It will be voided automatically. Revenue will be reversed.</div>
            )}
            <div className="form-group">
              <label className="form-label">Cancellation Reason *</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {['Customer cancelled order', 'Customer could not pay on delivery — goods returned', 'Out of stock', 'Pricing dispute', 'Delivery not possible on requested date'].map(r => (
                  <button key={r} onClick={() => setCancelReason(r)} style={{ padding: '4px 10px', fontSize: '0.72rem', borderRadius: 20, border: `1px solid ${cancelReason===r?'var(--amber)':'var(--border)'}`, background: cancelReason===r?'var(--amber-pale)':'white', cursor: 'pointer', fontFamily: 'inherit' }}>{r}</button>
                ))}
              </div>
              <textarea className="form-textarea" style={{ minHeight: 60 }} value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Describe the reason for cancellation..." />
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setCancelModal(null)}>Keep Order</button>
              <button className="btn btn-danger" onClick={handleCancel} disabled={!cancelReason.trim()}>🚫 Cancel Order</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
